import { useState } from "react";
import { Box, IconButton, Stack, TextField, Typography, Button } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Labeled, useRecordContext, useUpdate, useResourceContext, Validator } from "react-admin";
import { DateRange, ValidAny } from "@/types";
import { formatDateRange } from "@/utils/formatters/dateFormatter";

interface DateRangeArrayFieldProps {
  source: string;
  label?: string;
  helperText?: string;
  resource?: string;
  readOnly?: boolean;
  readOnlyReason?: string;
  validate?: Validator | Validator[];
}

/** Gets nested value from object by dot-separated path */
const getByPath = (obj: ValidAny, path: string): ValidAny =>
  path.split(".").reduce((acc, k) => (acc === null || acc === undefined ? acc : acc[k]), obj);

/** Sets nested value in object by dot-separated path, returns new object */
const setByPath = (obj: ValidAny, path: string, value: ValidAny): ValidAny => {
  const keys = path.split(".");
  const result = { ...obj };
  let current: ValidAny = result;

  for (let i = 0; i < keys.length - 1; i++) {
    current[keys[i]] = { ...current[keys[i]] };
    current = current[keys[i]];
  }

  current[keys[keys.length - 1]] = value;
  return result;
};

export const DateRangeArrayField = ({
  source,
  label,
  helperText,
  resource: resourceProp,
  readOnly = false,
  readOnlyReason,
  validate,
}: DateRangeArrayFieldProps) => {
  const record = useRecordContext();
  const contextResource = useResourceContext();
  const resource = resourceProp || contextResource;
  const [update, { isLoading }] = useUpdate();

  const [isEditing, setIsEditing] = useState(false);
  const [editedRanges, setEditedRanges] = useState<DateRange[]>([]);
  const [validationError, setValidationError] = useState<string | undefined>();

  /** Run validate prop against a value, return first error or undefined */
  const runValidation = (value: ValidAny): string | undefined => {
    if (!validate) return undefined;
    const validators = Array.isArray(validate) ? validate : [validate];
    for (const v of validators) {
      const result = v(value, {}, {});
      if (typeof result === "string") return result;
      if (result && typeof result === "object" && "message" in result) return result.message;
    }
    return undefined;
  };

  const ranges: DateRange[] = getByPath(record, source) || [];

  const handleStartEdit = () => {
    if (readOnly || readOnlyReason) return;
    setEditedRanges([...ranges]);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedRanges([]);
  };

  const handleSave = () => {
    // Filter out empty ranges
    const cleanedRanges = editedRanges.filter((r) => r.start_date || r.end_date);

    // Built-in: start date must not be after end date
    const invalidRange = cleanedRanges.find((r) => r.start_date && r.end_date && r.start_date > r.end_date);
    if (invalidRange) {
      setValidationError("Start date must be on or before end date");
      return;
    }

    const error = runValidation(cleanedRanges);
    if (error) {
      setValidationError(error);
      return;
    }

    const updatedData = setByPath(record, source, cleanedRanges);

    void update(
      resource,
      {
        id: record?.id,
        data: updatedData,
        previousData: record,
      },
      {
        onSuccess: () => setIsEditing(false),
      }
    );
  };

  const handleAddRange = () => {
    setEditedRanges([...editedRanges, { start_date: "", end_date: "" }]);
  };

  const handleRemoveRange = (index: number) => {
    setEditedRanges(editedRanges.filter((_, i) => i !== index));
  };

  const handleRangeChange = (index: number, field: "start_date" | "end_date", value: string) => {
    setValidationError(undefined);
    const updated = [...editedRanges];
    updated[index] = { ...updated[index], [field]: value };
    setEditedRanges(updated);
  };

  const canEdit = !readOnly && !readOnlyReason;

  // Read-only display — matches InPlaceEditor's click-to-edit pattern:
  // no visible edit icon, the value itself is clickable with hover highlight.
  if (!isEditing) {
    return (
      <Labeled label={label}>
        <Box
          onClick={canEdit ? handleStartEdit : undefined}
          title={!readOnly && readOnlyReason ? readOnlyReason : undefined}
          sx={{
            cursor: canEdit ? "pointer" : "default",
            "&:hover": canEdit ? { backgroundColor: "action.hover" } : undefined,
          }}
        >
          {ranges.length === 0 ? (
            <Typography variant="body1" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              -
            </Typography>
          ) : (
            <Stack spacing={0.5} sx={{ mb: 0.5 }}>
              {ranges.map((range, i) => (
                <Typography key={i} variant="body1">
                  {formatDateRange(range.start_date, range.end_date)}
                </Typography>
              ))}
            </Stack>
          )}
          {helperText && (
            <Typography variant="caption" color="text.secondary">
              {helperText}
            </Typography>
          )}
        </Box>
      </Labeled>
    );
  }

  // Edit mode
  return (
    <Labeled label={label}>
      <Box>
        <Stack spacing={1}>
          {editedRanges.map((range, index) => (
            <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                type="date"
                size="small"
                label="Start"
                value={range.start_date || ""}
                onChange={(e) => handleRangeChange(index, "start_date", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <Typography variant="body2" color="text.secondary">
                to
              </Typography>
              <TextField
                type="date"
                size="small"
                label="End"
                value={range.end_date || ""}
                onChange={(e) => handleRangeChange(index, "end_date", e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ width: 150 }}
              />
              <IconButton size="small" onClick={() => handleRemoveRange(index)} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Stack>

        {validationError && (
          <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
            {validationError}
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={handleAddRange}>
            Add Period
          </Button>
          <Box sx={{ flex: 1 }} />
          <IconButton size="small" onClick={handleCancel} disabled={isLoading}>
            <CloseIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={handleSave} disabled={isLoading} color="primary">
            <CheckIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Labeled>
  );
};
